import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Paleta de colores corporativos (misma que DISC/Raven)
const COLORES = {
    NARANJA: '#fe7f2d',
    VERDE: '#297373',
    MORADO: '#6b3fa0',
    AZUL: '#0ea5e9',
    GRIS_CLARO: '#f8f9fa',
    GRIS_MEDIO: '#e9ecef',
    TEXTO: '#212529',
    TEXTO_SUAVE: '#6c757d'
};

// Helpers de color
const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
    ] : [0, 0, 0];
};

// Función para cargar y agregar el logo
const cargarYAgregarLogo = (doc, pageWidth) => {
    return new Promise((resolve) => {
        try {
            const logo = new Image();
            logo.src = window.location.origin + '/logopartner.png';

            logo.onload = function () {
                const logoHeight = 18;
                const logoWidth = (logo.naturalWidth / logo.naturalHeight) * logoHeight;
                const logoX = (pageWidth - logoWidth) / 2;
                doc.addImage(logo, 'JPEG', logoX, 11, logoWidth, logoHeight);
                resolve(true);
            };

            logo.onerror = function () {
                console.warn('No se pudo cargar el logo, usando fallback de texto');
                doc.setTextColor(...hexToRgb(COLORES.NARANJA));
                doc.setFontSize(18);
                doc.setFont('helvetica', 'bold');
                doc.text('BPO PERU', pageWidth / 2, 20, { align: 'center' });
                resolve(false);
            };

            setTimeout(() => {
                if (!logo.complete) {
                    console.warn('Timeout cargando logo, usando fallback');
                    doc.setTextColor(...hexToRgb(COLORES.NARANJA));
                    doc.setFontSize(18);
                    doc.setFont('helvetica', 'bold');
                    doc.text('BPO PERU', pageWidth / 2, 20, { align: 'center' });
                    resolve(false);
                }
            }, 1000);

        } catch (error) {
            console.error('Error cargando logo:', error);
            doc.setTextColor(...hexToRgb(COLORES.NARANJA));
            doc.setFontSize(18);
            doc.setFont('helvetica', 'bold');
            doc.text('BPO PERU', pageWidth / 2, 20, { align: 'center' });
            resolve(false);
        }
    });
};

// Colores por nivel (IPV)
const obtenerColorNivel = (nivel) => {
    const colores = {
        ALTO: '#10b981',      // Verde
        PROMEDIO: '#f59e0b',  // Amarillo
        BAJO: '#ef4444'       // Rojo
    };
    return colores[nivel] || '#6b7280';
};

// Función principal para generar PDF de IPV
export const generarPDFIpv = async (resultado, returnDoc = false) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);
    let yPos = 0;
    let pageNum = 1;

    // Función para agregar header profesional
    const agregarHeader = async () => {
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, pageWidth, 35, 'F');

        // Barra superior naranja
        doc.setFillColor(...hexToRgb(COLORES.NARANJA));
        doc.rect(0, 0, pageWidth, 3, 'F');

        // Barra inferior verde
        doc.setFillColor(...hexToRgb(COLORES.VERDE));
        doc.rect(0, 32, pageWidth, 3, 'F');

        await cargarYAgregarLogo(doc, pageWidth);
    };

    // Footer profesional
    const agregarFooter = (pageNum) => {
        doc.setFillColor(...hexToRgb(COLORES.NARANJA));
        doc.rect(0, pageHeight - 15, pageWidth, 2, 'F');

        doc.setFillColor(...hexToRgb(COLORES.VERDE));
        doc.rect(0, pageHeight - 13, pageWidth, 13, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(`Página ${pageNum}`, pageWidth / 2, pageHeight - 7, { align: 'center' });
        
        const fecha = new Date().toLocaleDateString('es-PE');
        doc.text(`Generado el ${fecha}`, margin, pageHeight - 7);
    };

    // Verificar si necesitamos nueva página
    const verificarNuevaPagina = (alturaRequerida) => {
        if (yPos + alturaRequerida > pageHeight - 20) {
            agregarFooter(pageNum);
            doc.addPage();
            pageNum++;
            agregarHeader();
            yPos = 45;
            return true;
        }
        return false;
    };

    // ============ PÁGINA 1: PORTADA ============
    await agregarHeader();
    yPos = 50;

    // Título del reporte
    doc.setFillColor(...hexToRgb(COLORES.MORADO));
    doc.rect(margin, yPos, contentWidth, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('REPORTE DE EVALUACIÓN IPV', pageWidth / 2, yPos + 8, { align: 'center' });
    yPos += 20;

    doc.setTextColor(...hexToRgb(COLORES.TEXTO));
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Inventario de Personalidad para Vendedores', pageWidth / 2, yPos, { align: 'center' });
    yPos += 20;

    // Información del evaluado
    doc.setFillColor(...hexToRgb(COLORES.GRIS_CLARO));
    doc.rect(margin, yPos, contentWidth, 8, 'F');
    doc.setTextColor(...hexToRgb(COLORES.MORADO));
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('INFORMACIÓN DEL EVALUADO', margin + 3, yPos + 5);
    yPos += 12;

    doc.setTextColor(...hexToRgb(COLORES.TEXTO));
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const infoEvaluado = [
        ['Postulante:', resultado.nombrePostulante || resultado.usuarioNombre || `ID: ${resultado.postulanteId}`],
        ['Fecha:', new Date(resultado.fechaEvaluacion).toLocaleDateString('es-PE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })]
    ];

    infoEvaluado.forEach(([label, value]) => {
        doc.setFont('helvetica', 'bold');
        doc.text(label, margin + 5, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(value, margin + 40, yPos);
        yPos += 7;
    });

    yPos += 10;

    // DGV Destacado (Resultado Principal)
    doc.setFillColor(...hexToRgb(COLORES.MORADO));
    doc.rect(margin, yPos, contentWidth, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('RESULTADO PRINCIPAL - DGV', margin + 3, yPos + 5);
    yPos += 12;

    // Card DGV
    doc.setDrawColor(...hexToRgb(obtenerColorNivel(resultado.dgv.nivel)));
    doc.setLineWidth(1);
    doc.roundedRect(margin, yPos, contentWidth, 45, 2, 2, 'D');

    doc.setFontSize(12);
    doc.setTextColor(...hexToRgb(COLORES.TEXTO));
    doc.setFont('helvetica', 'bold');
    doc.text('DGV - Disposición General para la Venta', margin + 5, yPos + 7);

    // Nivel con color
    const colorNivel = obtenerColorNivel(resultado.dgv.nivel);
    doc.setFillColor(...hexToRgb(colorNivel));
    doc.roundedRect(margin + 5, yPos + 10, 25, 8, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(resultado.dgv.nivel, margin + 17.5, yPos + 15, { align: 'center' });

    // Puntuaciones
    doc.setTextColor(...hexToRgb(COLORES.TEXTO_SUAVE));
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Pd: ${resultado.dgv.puntuacionDirecta} | Pt: ${resultado.dgv.puntuacionTransformada} | Decatipo: ${resultado.dgv.decatipo}`, 
             margin + 35, yPos + 15);

    // Interpretación
    yPos += 22;
    doc.setTextColor(...hexToRgb(COLORES.TEXTO));
    doc.setFontSize(9);
    const interpretacionDGV = doc.splitTextToSize(resultado.dgv.interpretacion, contentWidth - 10);
    doc.text(interpretacionDGV, margin + 5, yPos);
    yPos += (interpretacionDGV.length * 4) + 15;

    agregarFooter(pageNum);

    // ============ PÁGINA 2+: ESCALAS DETALLADAS ============
    doc.addPage();
    pageNum++;
    await agregarHeader();
    yPos = 45;

    // Título de sección
    doc.setFillColor(...hexToRgb(COLORES.MORADO));
    doc.rect(margin, yPos, contentWidth, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('ANÁLISIS DETALLADO POR ESCALA', margin + 3, yPos + 5);
    yPos += 15;

    // Escalas IPV
    const escalas = [
        { key: 'receptividad', nombre: 'R - Receptividad' },
        { key: 'agresividad', nombre: 'A - Agresividad' },
        { key: 'comprension', nombre: 'I - Comprensión' },
        { key: 'adaptabilidad', nombre: 'II - Adaptabilidad' },
        { key: 'controlSiMismo', nombre: 'III - Control de Sí Mismo' },
        { key: 'toleranciaFrustracion', nombre: 'IV - Tolerancia a la Frustración' },
        { key: 'combatividad', nombre: 'V - Combatividad' },
        { key: 'dominancia', nombre: 'VI - Dominancia' },
        { key: 'seguridad', nombre: 'VII - Seguridad' },
        { key: 'actividad', nombre: 'VIII - Actividad' },
        { key: 'sociabilidad', nombre: 'IX - Sociabilidad' }
    ];

    escalas.forEach((escala) => {
        const datos = resultado[escala.key];
        if (!datos) return;

        // Verificar espacio para la escala completa
        const alturaEscala = 35;
        verificarNuevaPagina(alturaEscala);

        // Card de escala
        doc.setDrawColor(...hexToRgb(obtenerColorNivel(datos.nivel)));
        doc.setLineWidth(0.5);
        doc.roundedRect(margin, yPos, contentWidth, alturaEscala, 2, 2, 'D');

        // Nombre de la escala
        doc.setFontSize(10);
        doc.setTextColor(...hexToRgb(COLORES.TEXTO));
        doc.setFont('helvetica', 'bold');
        doc.text(datos.nombreEscala, margin + 3, yPos + 5);

        // Nivel badge
        const colorNivel = obtenerColorNivel(datos.nivel);
        doc.setFillColor(...hexToRgb(colorNivel));
        doc.roundedRect(contentWidth - 15, yPos + 2, 20, 6, 1, 1, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text(datos.nivel, contentWidth - 5, yPos + 5.5, { align: 'center' });

        // Puntuaciones
        doc.setTextColor(...hexToRgb(COLORES.TEXTO_SUAVE));
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(`Pd: ${datos.puntuacionDirecta} | Pt: ${datos.puntuacionTransformada} | Decatipo: ${datos.decatipo}`, 
                 margin + 3, yPos + 10);

        // Interpretación
        doc.setTextColor(...hexToRgb(COLORES.TEXTO));
        doc.setFontSize(8);
        const interpretacion = doc.splitTextToSize(datos.interpretacion, contentWidth - 6);
        doc.text(interpretacion, margin + 3, yPos + 15);

        yPos += alturaEscala + 5;
    });

    // Footer final
    agregarFooter(pageNum);

    // ============ PÁGINA FINAL: TABLA RESUMEN ============
    doc.addPage();
    pageNum++;
    await agregarHeader();
    yPos = 45;

    doc.setFillColor(...hexToRgb(COLORES.MORADO));
    doc.rect(margin, yPos, contentWidth, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('TABLA RESUMEN DE RESULTADOS', margin + 3, yPos + 5);
    yPos += 12;

    // Preparar datos para la tabla
    const todasLasEscalas = [
        { ...resultado.dgv, codigo: 'DGV' },
        { ...resultado.receptividad, codigo: 'R' },
        { ...resultado.agresividad, codigo: 'A' },
        { ...resultado.comprension, codigo: 'I' },
        { ...resultado.adaptabilidad, codigo: 'II' },
        { ...resultado.controlSiMismo, codigo: 'III' },
        { ...resultado.toleranciaFrustracion, codigo: 'IV' },
        { ...resultado.combatividad, codigo: 'V' },
        { ...resultado.dominancia, codigo: 'VI' },
        { ...resultado.seguridad, codigo: 'VII' },
        { ...resultado.actividad, codigo: 'VIII' },
        { ...resultado.sociabilidad, codigo: 'IX' }
    ];

    const tableData = todasLasEscalas.map(escala => [
        escala.codigo,
        escala.nombreEscala,
        escala.puntuacionDirecta,
        escala.puntuacionTransformada,
        escala.decatipo,
        escala.nivel
    ]);

    autoTable(doc, {
        startY: yPos,
        head: [['Código', 'Escala', 'Pd', 'Pt', 'Decatipo', 'Nivel']],
        body: tableData,
        theme: 'grid',
        headStyles: {
            fillColor: hexToRgb(COLORES.MORADO),
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 9
        },
        styles: {
            fontSize: 8,
            cellPadding: 3
        },
        columnStyles: {
            0: { cellWidth: 15, halign: 'center' },
            1: { cellWidth: 70 },
            2: { cellWidth: 15, halign: 'center' },
            3: { cellWidth: 15, halign: 'center' },
            4: { cellWidth: 18, halign: 'center' },
            5: { cellWidth: 25, halign: 'center' }
        },
        didParseCell: (data) => {
            // Colorear la columna de nivel
            if (data.column.index === 5 && data.section === 'body') {
                const nivel = data.cell.text[0];
                const color = obtenerColorNivel(nivel);
                data.cell.styles.fillColor = hexToRgb(color);
                data.cell.styles.textColor = [255, 255, 255];
                data.cell.styles.fontStyle = 'bold';
            }
        }
    });

    agregarFooter(pageNum);

    // Si se solicita retornar el doc (para combinar PDFs), no guardar
    if (returnDoc) {
        return doc;
    }

    // Guardar el PDF usando Blob para evitar refresh
    const nombrePostulante = resultado.nombrePostulante || resultado.usuarioNombre || `Usuario_${resultado.postulanteId}`;
    const nombreArchivo = `IPV_${nombrePostulante.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    const pdfBlob = doc.output('blob');
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nombreArchivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export default generarPDFIpv;
